import React, { useMemo, useState, useRef, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import { apiService } from "../services/apiService";
import { Select } from "antd";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

// Excel-like vertical borders, matching the ActualSalesSummaryNew look.
const gridStyles = `
  .sbu-grid .ag-root {
    --ag-borders-side-color: #d0d0d0;
  }

  .sbu-grid .ag-cell {
    border-right: 1px solid #d0d0d0 !important;
    border-bottom: 1px solid #d0d0d0 !important;
    display: flex !important;
    align-items: center !important;
    font-size: 12px !important;
    padding-left: 8px !important;
    padding-right: 8px !important;
  }

  .sbu-grid .ag-header-cell,
  .sbu-grid .ag-header-group-cell {
    border-right: 1px solid #d0d0d0 !important;
    border-bottom: 1px solid #d0d0d0 !important;
    background-color: #f5f5f5 !important;
    font-weight: 600 !important;
    color: #333 !important;
    font-size: 11.5px !important;
  }

  /* Center-align every header label (leaf + group) */
  .sbu-grid .ag-header-cell-label,
  .sbu-grid .ag-header-group-cell-label {
    justify-content: center !important;
    text-align: center !important;
  }

  .sbu-grid .ag-pinned-left-header .ag-header-cell-text {
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }

  .sbu-grid .ag-header-cell-text {
    overflow: visible !important;
  }

  /* Merged dimension cells sit at the top of their block */
  .sbu-grid .span-cell {
    align-items: flex-start !important;
    padding-top: 6px !important;
    background-color: #ffffff !important;
    font-weight: 600 !important;
  }

  /* Continuation rows of a merged cell: drop internal borders so the block
     reads as one area rather than stacked cells. */
  .sbu-grid .dim-empty {
    border-top: none !important;
    border-bottom: none !important;
    background-color: #ffffff !important;
  }

  /* The "All" bucket row is a normal row — not emphasized. The .ag-cell prefix
     raises specificity so it wins over the per-column "Total" styling. */
  .sbu-grid .ag-cell.all-row-cell {
    font-weight: 400 !important;
    background-color: #f7f9fc !important;
  }

  /* The per-block "Total" row is the emphasized (bold) one, across every column. */
  .sbu-grid .ag-cell.total-row-cell {
    font-weight: 700 !important;
    background-color: #eef4fb !important;
  }

  .sbu-grid .market-cell {
    background-color: #fafafa !important;
  }

  /* Per-month "Total" column and the grand total column */
  .sbu-grid .total-col-cell {
    background-color: #f7f7f7 !important;
    font-weight: 600 !important;
  }

  /* Pinned TOTAL footer row */
  .sbu-grid .ag-floating-bottom .ag-cell {
    font-weight: 700 !important;
    background-color: #e6eef7 !important;
  }
`;

if (typeof document !== "undefined" && !document.getElementById("sbu-grid-styles")) {
  const styleSheet = document.createElement("style");
  styleSheet.id = "sbu-grid-styles";
  styleSheet.textContent = gridStyles;
  document.head.appendChild(styleSheet);
}

ModuleRegistry.registerModules([AllCommunityModule]);

const formatMoney = (value) => {
  const num = Number(value) || 0;
  return `$${num.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

/* ===============================
   API SHAPE (actual-sales/get-sbu-matrix)

   {
     year, quota,
     monthColumns: [{ month, year, label, key }],   // key = "Apr-2026"
     sbus:    ["CM", "ODM", "Own Products", "WSBG"],
     columns: [...sbus, "Total"],
     customers: [...], businesses: [...], markets: [..., "All"],
     data: [
       { customer, total, months: { "Apr-2026": { sbus: {...}, total } },
         businesses: [
           { business, total, months: {...},
             markets: [ { market, total, months: {...} } ] }
         ] }
     ],
     total: { total, months: {...} }   // grand total -> pinned TOTAL row
   }
=============================== */

// A segment row is meaningless when its row total and every month's SBU
// amounts are all zero.
const isEmptyMarket = (mk, monthKeys) => {
  if (Number(mk.total) !== 0) return false;
  return monthKeys.every((key) =>
    Object.values(mk.months?.[key]?.sbus || {}).every((v) => Number(v) === 0)
  );
};

// Drop all-zero segments, then drop the businesses/customers left with none.
// Pruning the nested shape (not the flat rows) keeps the rowSpan counts in
// step with the rows that actually render.
const pruneEmpty = (matrix) => {
  const monthKeys = (matrix.monthColumns || []).map((c) => c.key);
  const data = (matrix.data || [])
    .map((cust) => ({
      ...cust,
      businesses: (cust.businesses || [])
        .map((bus) => ({
          ...bus,
          markets: (bus.markets || []).filter((mk) => !isEmptyMarket(mk, monthKeys)),
        }))
        .filter((bus) => bus.markets.length > 0),
    }))
    .filter((cust) => cust.businesses.length > 0);

  return { ...matrix, data };
};

/* ===============================
   TRANSFORM: nested matrix -> flat rows w/ rowSpan
=============================== */
const buildRows = (rawMatrix, hideEmpty) => {
  const matrix = hideEmpty ? pruneEmpty(rawMatrix) : rawMatrix;
  const monthColumns = matrix.monthColumns || [];
  const sbus = matrix.sbus || [];
  const rows = [];

  (matrix.data || []).forEach((cust) => {
    // Rows this customer block spans = every market under every business.
    const custRowCount = (cust.businesses || []).reduce(
      (sum, b) => sum + (b.markets || []).length,
      0
    );
    let custFirst = true;

    (cust.businesses || []).forEach((bus) => {
      const markets = bus.markets || [];
      const busRowCount = markets.length;
      let busFirst = true;

      // Every market row — real segments, the unsegmented "All" bucket, and the
      // backend's per-block "Total" (= segments + All) — is rendered straight from
      // the API values. The backend is the single source of truth; the frontend no
      // longer recomputes the "All" row (that double-counted the "Total" row into
      // it). The grand-total footer is built in buildTotalRow.
      markets.forEach((mk) => {
        const row = {
          customer: custFirst ? cust.customer : "",
          customerSpan: custFirst ? custRowCount : 0,

          business: busFirst ? bus.business : "",
          businessSpan: busFirst ? busRowCount : 0,

          market: mk.market,
          isAllRow: mk.market === "All",
          isTotalRow: mk.market === "Total",
          grandTotal: mk.total || 0,
        };

        // Each month contributes one cell per SBU plus that month's total.
        // SBU names carry spaces ("Own Products"), so index them positionally.
        monthColumns.forEach((col, i) => {
          const cell = mk.months?.[col.key] || {};
          sbus.forEach((sbu, j) => {
            row[`m${i}_s${j}`] = cell.sbus?.[sbu] ?? 0;
          });
          row[`m${i}_total`] = cell.total ?? 0;
        });

        rows.push(row);
        custFirst = false;
        busFirst = false;
      });
    });
  });

  return rows;
};

// The grand total renders as a pinned bottom row. Each Exist/New block already
// carries a backend "Total" row (= its segments + the "All" bucket), so the
// footer is the vertical sum of those per-block "Total" rows only. Summing the
// "Total" rows (rather than every segment + All) is what keeps it from
// double-counting.
const buildTotalRow = (rows, monthColumns, sbus) => {
  if (!monthColumns.length) return [];

  const row = { customer: "TOTAL", business: "", market: "", grandTotal: 0 };
  monthColumns.forEach((col, i) => {
    sbus.forEach((sbu, j) => {
      row[`m${i}_s${j}`] = 0;
    });
    row[`m${i}_total`] = 0;
  });

  rows.forEach((r) => {
    if (r.market !== "Total") return; // sum the per-block "Total" rows only
    row.grandTotal += Number(r.grandTotal) || 0;
    monthColumns.forEach((col, i) => {
      sbus.forEach((sbu, j) => {
        row[`m${i}_s${j}`] += Number(r[`m${i}_s${j}`]) || 0;
      });
      row[`m${i}_total`] += Number(r[`m${i}_total`]) || 0;
    });
  });

  return [row];
};

const getCurrentQuarter = () => {
  const month = new Date().getMonth() + 1; // 1-12
  if (month <= 3) return "Q4";
  if (month <= 6) return "Q1";
  if (month <= 9) return "Q2";
  return "Q3";
};

const ActualSalesSbuSummary = () => {
  const [loading, setLoading] = useState(true);
  const [gridRowData, setGridRowData] = useState([]);
  const [totalRowData, setTotalRowData] = useState([]);
  const [monthColumns, setMonthColumns] = useState([]);
  const [sbus, setSbus] = useState([]);
  const [hideEmptyRows, setHideEmptyRows] = useState(true);

  // Last fetched matrix, so toggling hideEmptyRows rebuilds without a refetch.
  const matrixRef = useRef({});

  const currentYear = new Date().getFullYear();
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedQuarter, setSelectedQuarter] = useState(getCurrentQuarter());

  const gridRef = useRef(null);

  /* ===============================
     FETCH
  =============================== */
  const fetchSbuMatrix = async () => {
    setLoading(true);
    try {
      const response = await apiService("actual-sales/get-sbu-matrix", "POST", {
        year: selectedYear,
        quota: selectedQuarter,
      });

      const matrix = response?.data || {};
      matrixRef.current = matrix;
      setMonthColumns(matrix.monthColumns || []);
      setSbus(matrix.sbus || []);
      const rows = buildRows(matrix, hideEmptyRows);
      setGridRowData(rows);
      setTotalRowData(buildTotalRow(rows, matrix.monthColumns || [], matrix.sbus || []));
    } catch (error) {
      console.error("Error fetching SBU matrix:", error);
      matrixRef.current = {};
      setMonthColumns([]);
      setSbus([]);
      setGridRowData([]);
      setTotalRowData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchYears = async () => {
    try {
      const response = await apiService("actual-sales/years", "GET");
      if (response.data && response.statusCode === 200) {
        const years = response.data || [];
        setAvailableYears(years);
        if (years.length > 0) setSelectedYear(years[0]);
      }
    } catch (error) {
      console.error("Error fetching years:", error);
    }
  };

  useEffect(() => {
    fetchYears();
  }, []);

  useEffect(() => {
    fetchSbuMatrix();
  }, [selectedYear, selectedQuarter]);

  const toggleHideEmptyRows = (checked) => {
    setHideEmptyRows(checked);
    const rows = buildRows(matrixRef.current, checked);
    setGridRowData(rows);
    setTotalRowData(
      buildTotalRow(rows, matrixRef.current.monthColumns || [], matrixRef.current.sbus || [])
    );
  };

  /* ===============================
     COLUMN DEFINITIONS
     Ext/Int | Exist/New | Segment | <month>: [SBU...] + Total | Total
  =============================== */
  const columnDefs = useMemo(() => {
    const moneyCol = (headerName, field, isTotal) => ({
      headerName,
      field,
      flex: 1,
      minWidth: 110,
      type: "rightAligned",
      valueFormatter: (p) => formatMoney(p.value),
      cellClassRules: {
        "total-col-cell": () => Boolean(isTotal),
        "all-row-cell": (p) => Boolean(p.data?.isAllRow),
        "total-row-cell": (p) => Boolean(p.data?.isTotalRow),
      },
    });

    const monthGroups = monthColumns.map((col, i) => ({
      headerName: col.key,
      marryChildren: true,
      children: [
        ...sbus.map((sbu, j) => moneyCol(sbu, `m${i}_s${j}`, false)),
        moneyCol("Total", `m${i}_total`, true),
      ],
    }));

    // The pinned TOTAL row prints its label in the Ext/Int column and must not
    // inherit a rowSpan from the (absent) span fields.
    const dimensionCol = (headerName, field, spanField, width) => ({
      headerName,
      field,
      width,
      pinned: "left",
      rowSpan: (p) => (p.node?.rowPinned ? 1 : p.data?.[spanField] || 1),
      cellClassRules: {
        "span-cell": (p) => !p.node?.rowPinned && (p.data?.[spanField] || 0) > 0,
        "dim-empty": (p) => !p.node?.rowPinned && !(p.data?.[spanField] || 0),
      },
    });

    return [
      {
        headerName: "",
        children: [
          dimensionCol("Ext/Int", "customer", "customerSpan", 110),
          dimensionCol("Exist/New", "business", "businessSpan", 110),
          {
            headerName: "Segment",
            field: "market",
            width: 170,
            pinned: "left",
            cellClass: (p) =>
              p.node?.rowPinned
                ? ""
                : p.data?.isTotalRow
                ? "total-row-cell"
                : p.data?.isAllRow
                ? "all-row-cell"
                : "market-cell",
          },
        ],
      },
      ...monthGroups,
      {
        headerName: "",
        children: [moneyCol("Grand Total", "grandTotal", true)],
      },
    ];
  }, [monthColumns, sbus]);

  /* ===============================
     RENDER
  =============================== */
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", width: "100%" }}>
      <div
        style={{
          padding: "15px 20px",
          display: "flex",
          gap: "15px",
          alignItems: "center",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <div>
          <label style={{ marginRight: "8px", fontWeight: "bold" }}>Year:</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            style={{
              padding: "8px 12px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              fontSize: "14px",
              cursor: "pointer",
            }}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ marginRight: "8px", fontWeight: "bold" }}>Quarter:</label>
          <Select
            placeholder="Select Quarter"
            value={selectedQuarter}
            onChange={(value) => setSelectedQuarter(value)}
            style={{ width: 200 }}
            options={[
              { label: "Q1 (Apr-Jun)", value: "Q1" },
              { label: "Q2 (Jul-Sep)", value: "Q2" },
              { label: "Q3 (Oct-Dec)", value: "Q3" },
              { label: "Q4 (Jan-Mar)", value: "Q4" },
            ]}
          />
        </div>

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            cursor: "pointer",
            fontWeight: "bold",
          }}
        >
          <input
            type="checkbox"
            checked={hideEmptyRows}
            onChange={(e) => toggleHideEmptyRows(e.target.checked)}
            style={{ cursor: "pointer" }}
          />
          Hide empty rows
        </label>
      </div>

      <div
        className="ag-theme-alpine sbu-grid"
        style={{
          flex: 1,
          minHeight: 0,
          width: "100%",
          border: "1px solid #d0d0d0",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={gridRowData}
          pinnedBottomRowData={totalRowData}
          columnDefs={columnDefs}
          suppressRowTransform={true}
          loading={loading}
          suppressSortingAndFiltering={true}
          domLayout="normal"
          defaultColDef={{
            resizable: true,
            sortable: false,
            editable: false,
          }}
          rowHeight={34}
          headerHeight={38}
          wrapHeaderText={true}
          autoHeaderHeight={true}
        />
      </div>
    </div>
  );
};

export default ActualSalesSbuSummary;
